import { useContainersApi } from "@src/api/issuance/useContainersApi";
import { useMemo } from "react";
import { useMerits } from "@src/api/issuance/useMerits";
import type { Container } from "@merit/issuance-client";
import type { Merit } from "@src/api/issuance/types";

type UseMeritDetailsParams = {
  readonly meritId: Container["id"];
};

export enum State {
  NOT_READY = "notReady",
  LOADING = "loading",
  ERROR = "error",
  FOUND = "found",
  NOT_FOUND = "notFound",
}

type UseMeritDetailsReturn = {
  readonly matchingMerit: Merit | undefined;
  readonly state: State;
};

const useMeritDetails = ({ meritId }: UseMeritDetailsParams): UseMeritDetailsReturn => {
  const { isReady } = useContainersApi();
  const { data: merits, isError, isLoading } = useMerits();

  /**
   * undefined means matching hasn't been attempted
   * null means no match
   * otherwise, a match was found
   */
  const matchingMerit = useMemo(() => {
    if (merits === undefined) {
      return undefined;
    }

    return merits.find(container => container.id === meritId) ?? null;
  }, [merits, meritId]);

  const state = useMemo<State>(() => {
    if (!isReady) {
      return State.NOT_READY;
    }
    if (isLoading) {
      return State.LOADING;
    }
    if (isError) {
      return State.ERROR;
    }
    if (matchingMerit === undefined) {
      return State.LOADING;
    }
    if (merits === undefined || matchingMerit === null) {
      return State.NOT_FOUND;
    }

    return State.FOUND;
  }, [isError, isLoading, isReady, matchingMerit, merits]);

  return {
    matchingMerit: matchingMerit ?? undefined,
    state,
  };
};

export { useMeritDetails };
